import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/auth/AuthProvider'
import { FxAlert, FxButton, FxIcon, FxSpinner, FxTextField } from '@/components/fx'
import { PageTitle } from '@/layouts/shellChrome'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'
import { QuestionBlock } from './QuestionBlock'
import { onboardingApi, type OnboardingResponse } from './onboardApi'
import {
  answeredCount,
  answersToSend,
  opensOnQuestions,
  parseQuestions,
  renderableCount,
  selectionsFrom,
  toggleOption,
  type Selections,
} from './onboardModel'

/**
 * "Vier kurze Fragen" — `/fragen`.
 *
 * The questions are **rendered from the server's catalogue**
 * (`GET /v1/auth/onboard` carries it beside the saved draft), so a question
 * can be reworded or retired without shipping a client. Nothing about the
 * wording, the option ids or the number of questions is hard-coded here;
 * `onboardModel.ts` holds the rules for a catalogue this build does not
 * fully understand.
 *
 * Two steps, as `Onboarding.dc.html` draws them. Step one is real rather
 * than decorative: `POST /v1/auth/onboard` takes `firstName`/`lastName` and
 * keeps the display name in step, so "Über dich" writes the name the rest of
 * the app greets the person by. Leaving step one gets saved as a draft
 * (`complete: false`), which is what makes the screen resumable — and why
 * `complete` is always sent: the gateway defaults it to **true**, so a
 * partial save that omitted it would be filed as a finished questionnaire.
 *
 * It is not inside `AppShell`: the artboard draws a full page with no
 * navigation, and the two ways out ("Fertig" and "Später beantworten") are
 * on the page.
 */
export function FragenPage() {
  const { user, reload } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const state = useQuery({
    queryKey: ['onboarding'],
    queryFn: () => onboardingApi.get(),
    staleTime: 60_000,
  })

  const [step, setStep] = useState<'name' | 'questions'>('name')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [nameError, setNameError] = useState<string | undefined>()
  const [selections, setSelections] = useState<Selections>({})

  const data: OnboardingResponse | undefined = state.data
  const questions = parseQuestions(data?.questions)

  // The draft arrives once, after the request. Keyed on `updatedAt` so a
  // background refetch that changed nothing cannot overwrite what is being
  // answered right now.
  const loadedAt = data ? `${data.status}|${data.updatedAt ?? ''}` : ''
  useEffect(() => {
    if (!data) return
    setSelections(selectionsFrom(data.answers, parseQuestions(data.questions)))
    // Somebody who has answered before came back to change something; making
    // them walk past their own name again would be a step for nothing.
    if (opensOnQuestions(data)) setStep('questions')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedAt])

  useEffect(() => {
    setFirstName(user?.firstName ?? '')
    setLastName(user?.lastName ?? '')
  }, [user?.firstName, user?.lastName])

  const save = useMutation({
    mutationFn: (complete: boolean) =>
      onboardingApi.save({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        answers: answersToSend(selections, questions),
        complete,
      }),
    onSuccess: (response) => {
      queryClient.setQueryData(['onboarding'], response)
      // The name reaches the rail's avatar and the greeting through the auth
      // context, not through this response.
      void reload()
    },
  })

  const dismiss = useMutation({
    mutationFn: () => onboardingApi.dismiss(),
    onSuccess: (response) => {
      queryClient.setQueryData(['onboarding'], response)
      navigate('/', { replace: true })
    },
  })

  function goToQuestions() {
    if (!firstName.trim()) {
      setNameError('Bitte geben Sie Ihren Vornamen an.')
      return
    }
    setNameError(undefined)
    setStep('questions')
    // Best effort: the draft keeps the screen resumable, and the final save
    // sends the name again, so a failure here costs nothing and must not
    // stand between somebody and the next step.
    save.mutate(false)
  }

  function finish() {
    save.mutate(true, { onSuccess: () => navigate('/', { replace: true }) })
  }

  const total = renderableCount(questions)
  const answered = answeredCount(selections, questions)
  const alreadyAnswered = data?.status === 'answered'

  return (
    <main className="min-h-screen bg-surface px-5 py-10 sm:px-10">
      <div className="mx-auto flex w-full max-w-[640px] flex-col gap-7">
        <Stepper step={step} />

        <div className="flex flex-col gap-3 text-center">
          <PageTitle>{step === 'name' ? 'Wer sind Sie?' : 'Womit arbeiten Sie?'}</PageTitle>
          <p className="text-body text-text">
            {step === 'name'
              ? 'Damit wir Sie richtig ansprechen. Sie können das jederzeit in den Einstellungen ändern.'
              : 'Damit wir Formalogix passend einrichten. Sie können alles später ändern.'}
          </p>
        </div>

        {state.isLoading && <FxSpinner label="Fragen werden geladen …" />}

        {state.isError && (
          <FxAlert tone="danger" title="Die Fragen konnten nicht geladen werden">
            <p>
              {ApiError.from(state.error).code === 'network_error'
                ? 'Keine Verbindung zum Server.'
                : 'Bitte versuchen Sie es noch einmal.'}
            </p>
            <div className="flex flex-wrap gap-4 pt-1">
              <FxButton variant="outlined" onClick={() => void state.refetch()}>
                Erneut versuchen
              </FxButton>
              <FxButton variant="tonal" onClick={() => navigate('/', { replace: true })}>
                Zur Übersicht
              </FxButton>
            </div>
          </FxAlert>
        )}

        {data && step === 'name' && (
          <div className="flex flex-col gap-5">
            <FxTextField
              label="Vorname"
              required
              onWhite
              value={firstName}
              autoComplete="given-name"
              error={nameError}
              onChange={(event) => setFirstName(event.target.value)}
            />
            <FxTextField
              label="Nachname"
              onWhite
              value={lastName}
              autoComplete="family-name"
              onChange={(event) => setLastName(event.target.value)}
            />
            <FxButton onClick={goToQuestions} block>
              Weiter
            </FxButton>
          </div>
        )}

        {data && step === 'questions' && (
          <>
            {total === 0 ? (
              <FxAlert tone="warning" title="Zurzeit gibt es hier keine Fragen">
                <p>
                  Diese Version kann die Fragen nicht anzeigen, die der Server geschickt hat. Sie
                  verpassen nichts — Sie können direkt weiterarbeiten.
                </p>
              </FxAlert>
            ) : (
              <>
                {alreadyAnswered && (
                  <FxAlert tone="info" title="Sie haben diese Fragen schon beantwortet">
                    <p>Sie können Ihre Antworten hier ändern.</p>
                  </FxAlert>
                )}
                <div className="flex flex-col gap-7">
                  {questions.map((question) => (
                    <QuestionBlock
                      key={question.id}
                      question={question}
                      selections={selections}
                      onToggle={(optionId) =>
                        setSelections((current) => toggleOption(current, question, optionId))
                      }
                      onSelect={(optionId) =>
                        setSelections((current) => ({
                          ...current,
                          [question.id]: optionId ? [optionId] : [],
                        }))
                      }
                    />
                  ))}
                </div>
                {/* No question is mandatory — the gateway stores whatever
                    arrives — so this counts rather than blocks. */}
                <p aria-live="polite" className="text-small text-text-muted">
                  {answered} von {total} Fragen beantwortet
                </p>
              </>
            )}

            {save.isError && (
              <p className="text-small text-danger-text">{saveErrorMessage(save.error)}</p>
            )}

            <div className="flex flex-col gap-4 sm:flex-row">
              <FxButton variant="tonal" className="sm:flex-1" onClick={() => setStep('name')}>
                Zurück
              </FxButton>
              <FxButton className="sm:flex-[2]" disabled={save.isPending} onClick={finish}>
                {save.isPending
                  ? 'Wird gespeichert …'
                  : alreadyAnswered
                    ? 'Änderungen speichern'
                    : 'Fertig'}
              </FxButton>
            </div>
          </>
        )}

        {data && (
          <div className="flex flex-col items-center gap-2">
            <FxButton
              variant="tonal"
              className="border-0 bg-transparent text-primary-text shadow-none hover:bg-primary-subtle"
              disabled={dismiss.isPending}
              onClick={() => dismiss.mutate()}
            >
              Später beantworten
            </FxButton>
            {dismiss.isError && (
              <p className="text-small text-danger-text">
                Das konnten wir gerade nicht speichern. Bitte versuchen Sie es noch einmal.
              </p>
            )}
          </div>
        )}
      </div>
    </main>
  )
}

function saveErrorMessage(error: unknown): string {
  const api = ApiError.from(error)
  if (api.code === 'network_error') return 'Keine Verbindung zum Server.'
  // `invalid_answers` and `invalid_body` are both client bugs — a shape the
  // gateway refuses — so they say the same thing to the person and are told
  // apart in the console, not on the screen.
  if (api.status === 400) return 'Ihre Antworten konnten so nicht gespeichert werden.'
  return 'Ihre Antworten konnten nicht gespeichert werden. Bitte versuchen Sie es noch einmal.'
}

/**
 * The two-step indicator from the artboard. `aria-current` marks the step
 * that is open; the finished one carries a check *and* the word, because the
 * glyph alone is a colour-and-shape signal.
 */
function Stepper({ step }: { step: 'name' | 'questions' }) {
  const steps = [
    { key: 'name', label: 'Über Sie' },
    { key: 'questions', label: 'Ihre Formulare' },
  ] as const
  const currentIndex = steps.findIndex((entry) => entry.key === step)

  return (
    <ol className="flex flex-wrap items-center justify-center gap-3 text-body">
      {steps.map((entry, index) => {
        const done = index < currentIndex
        const current = index === currentIndex
        return (
          <li key={entry.key} className="flex items-center gap-3">
            {index > 0 && <span aria-hidden className="h-0.5 w-10 bg-primary sm:w-[60px]" />}
            <span
              aria-hidden
              className={cn(
                'inline-flex h-8 w-8 flex-none items-center justify-center rounded-full text-small',
                done || current
                  ? 'bg-primary-strong text-on-primary'
                  : 'bg-fill text-text-on-disabled',
              )}
            >
              {done ? <FxIcon name="check" size={18} /> : index + 1}
            </span>
            <span
              aria-current={current ? 'step' : undefined}
              className={cn(current && 'font-semibold')}
            >
              {entry.label}
              {done && <span className="sr-only"> (abgeschlossen)</span>}
            </span>
          </li>
        )
      })}
    </ol>
  )
}
